import type { AuthTokens, AuthUser } from "@steelbill/types";

/**
 * Where the session lives between page loads (spec §11.1).
 *
 * §11.1: "Login: phone + 6-digit PIN, with 'stay logged in'. ... **Keep him
 * permanently signed in; the PIN is a last resort.**" That sentence is the
 * whole design of this file. The refresh token is valid for 30 days
 * (apps/api/src/auth/tokens.ts), the API reissues a fresh 30-day refresh token
 * on every refresh, and `stay logged in` defaults to ON — so an owner who opens
 * the app at least once a month is never asked for his PIN again.
 *
 * WHY `localStorage` AND NOT AN httpOnly COOKIE.
 * §1.1 and §12.5 already settled this: "Use bearer tokens rather than
 * cookie-only, so the Capacitor shell works identically." A Capacitor APK's
 * origin is not the server's, so a cookie scoped to the API host is simply not
 * sent. One storage mechanism that works in the browser and in the shell beats
 * two that each work in one place. The trade-off is XSS exposure, which is
 * mitigated by the app having no user-generated HTML anywhere and no
 * third-party scripts (§1.1 — "no runtime component library in the bundle").
 *
 * WHY TWO STORAGES.
 * "Stay logged in" ON → `localStorage`, survives closing the browser.
 * OFF → `sessionStorage`, gone when the tab closes. That is a real distinction
 * on the shared office desktop, where the son may not want the session to
 * outlive the tab. Reads check BOTH, so flipping the switch cannot orphan a
 * session in the storage that is no longer being written.
 *
 * NOTHING HERE PARSES OR TRUSTS THE TOKEN. The client never decodes the JWT to
 * decide anything: expiry is discovered by the API answering 401 (see
 * `lib/api.ts`), and authorisation is `can()` from `@steelbill/types` against
 * the role in the stored user record, which the server independently enforces
 * on every request (§2.1 — "hiding a button is not security").
 */

const ACCESS_TOKEN_KEY = "steelbill.accessToken";
const REFRESH_TOKEN_KEY = "steelbill.refreshToken";
const USER_KEY = "steelbill.user";
const STAY_SIGNED_IN_KEY = "steelbill.staySignedIn";

/** §11.1 — "keep him permanently signed in". The default is ON. */
export const STAY_SIGNED_IN_DEFAULT = true;

/** The session as the app holds it: the two tokens plus who is signed in. */
export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

/**
 * Storage access that never throws.
 *
 * Safari in private mode and a locked-down Android WebView both make
 * `localStorage` throw on WRITE while still existing. An uncaught throw here
 * would blank the app at login — the one screen where a crash is unrecoverable
 * because there is nothing to go back to.
 */
function safeStorage(kind: "local" | "session"): Storage | null {
  try {
    const storage = kind === "local" ? window.localStorage : window.sessionStorage;
    const probe = "__steelbill_probe__";
    storage.setItem(probe, "1");
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

function readRaw(key: string): string | null {
  for (const kind of ["local", "session"] as const) {
    const value = safeStorage(kind)?.getItem(key);
    if (typeof value === "string" && value !== "") return value;
  }
  return null;
}

function writeRaw(key: string, value: string, stay: boolean): void {
  // Write to the chosen storage and CLEAR the other, so a session can never
  // exist in both with different values.
  const target = safeStorage(stay ? "local" : "session");
  const other = safeStorage(stay ? "session" : "local");
  other?.removeItem(key);
  target?.setItem(key, value);
}

function removeRaw(key: string): void {
  safeStorage("local")?.removeItem(key);
  safeStorage("session")?.removeItem(key);
}

/* -------------------------------------------------------------------------- */
/* "Stay logged in"                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The preference itself always lives in `localStorage` — it has to outlive the
 * tab in order to pre-tick the checkbox on the next visit, which is the point.
 */
export function getStaySignedIn(): boolean {
  const stored = safeStorage("local")?.getItem(STAY_SIGNED_IN_KEY);
  if (stored === "false") return false;
  if (stored === "true") return true;
  return STAY_SIGNED_IN_DEFAULT;
}

export function setStaySignedIn(stay: boolean): void {
  safeStorage("local")?.setItem(STAY_SIGNED_IN_KEY, stay ? "true" : "false");
}

/* -------------------------------------------------------------------------- */
/* The session                                                                 */
/* -------------------------------------------------------------------------- */

export function getAccessToken(): string | null {
  return readRaw(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return readRaw(REFRESH_TOKEN_KEY);
}

/**
 * The signed-in user, or null.
 *
 * The stored JSON is validated structurally rather than cast: it is
 * attacker-writable in principle (it is in `localStorage`), and a malformed
 * record must produce "signed out", never a `user.role` of `undefined` reaching
 * `can()`. `can()` is itself deny-by-default, so this is defence in depth, and
 * the SERVER is the authority either way (§2.1).
 */
export function getStoredUser(): AuthUser | null {
  const raw = readRaw(USER_KEY);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      typeof (parsed as AuthUser).id === "number" &&
      typeof (parsed as AuthUser).role === "string" &&
      typeof (parsed as AuthUser).displayName === "string"
    ) {
      return parsed as AuthUser;
    }
  } catch {
    /* fall through — a corrupt record is a signed-out user, not a crash */
  }
  return null;
}

/** The whole session, or null if any part of it is missing. */
export function getStoredSession(): StoredSession | null {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  const user = getStoredUser();
  if (accessToken === null || refreshToken === null || user === null) return null;
  return { accessToken, refreshToken, user };
}

/**
 * Persist a session. Called after a successful login AND after every silent
 * refresh — the API rotates BOTH tokens on `/auth/refresh`
 * (apps/api/src/services/auth-service.ts), so storing only the new access
 * token would let the refresh token expire 30 days after the first login
 * instead of 30 days after the last use, and the owner would be asked for his
 * PIN out of nowhere. §11.1 says the PIN is a last resort; this is how that
 * stays true.
 *
 * `expiresIn` from the API is deliberately NOT stored. The client does not
 * schedule anything off it: the source of truth for "is this token still good"
 * is the API's 401, and a clock-based guess adds a second, disagreeing answer
 * (a phone whose clock is wrong is common, and a workshop phone that has been
 * off for a week is common too).
 */
export function saveSession(tokens: AuthTokens, stay: boolean = getStaySignedIn()): void {
  setStaySignedIn(stay);
  writeRaw(ACCESS_TOKEN_KEY, tokens.accessToken, stay);
  writeRaw(REFRESH_TOKEN_KEY, tokens.refreshToken, stay);
  writeRaw(USER_KEY, JSON.stringify(tokens.user), stay);
}

/** Sign out: remove from both storages. The preference survives. */
export function clearSession(): void {
  removeRaw(ACCESS_TOKEN_KEY);
  removeRaw(REFRESH_TOKEN_KEY);
  removeRaw(USER_KEY);
}
